import { useState } from "react";
import { tiposVistoria, statusVistoria } from "@/config/vistorias-config";
import { estadosFitossanitarios, classesRisco } from "@/config/cta-config";

interface InspectionFiltersProps {
  filters: Record<string, string>;
  onFilterChange: (key: string, value: string) => void;
  onClear: () => void;
  totalResults: number;
  projects: { id: string; nome: string }[];
  /** Tecnicos que aparecem nas vistorias carregadas. */
  tecnicos: string[];
}

export default function InspectionFilters({ filters, onFilterChange, onClear, totalResults, projects, tecnicos }: InspectionFiltersProps) {
  const [expanded, setExpanded] = useState(true);

  const hasActiveFilters = Object.values(filters).some((v) => v !== "");

  const selectClasses = "w-full bg-white border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors appearance-none cursor-pointer";

  return (
    <div className="bg-white rounded-xl border border-background-200/70 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-background-50 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
            <i className="ri-filter-3-line text-foreground-600"></i>
          </span>
          <span className="text-sm font-semibold text-foreground-900 font-heading">Filtros</span>
          {hasActiveFilters && (
            <span className="badge bg-primary-100 text-primary-700">{totalResults} resultados</span>
          )}
        </div>
        {expanded ? (
          <i className="ri-arrow-up-s-line text-foreground-400 transition-transform duration-200"></i>
        ) : (
          <i className="ri-arrow-down-s-line text-foreground-400 transition-transform duration-200"></i>
        )}
      </button>

      {expanded && (
        <div className="px-5 pb-4 space-y-3 border-t border-background-100">
          {/* Search */}
          <div>
            <label className="text-xs font-medium text-foreground-500 mb-1 block">Buscar</label>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                placeholder="Código CTA, espécie, técnico ou logradouro..."
                value={filters.search || ""}
                onChange={(e) => onFilterChange("search", e.target.value)}
                className="w-full bg-white border border-background-200/70 rounded-lg pl-9 pr-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
              />
            </div>
          </div>

          {/* Grid de filtros */}
          <div className="grid grid-cols-2 gap-3">
            {/* Projeto */}
            <div>
              <label className="text-xs font-medium text-foreground-500 mb-1 block">Projeto</label>
              <select
                value={filters.projeto || ""}
                onChange={(e) => onFilterChange("projeto", e.target.value)}
                className={selectClasses}
              >
                <option value="">Todos</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.nome}>{p.nome}</option>
                ))}
              </select>
            </div>

            {/* Tipo de vistoria */}
            <div>
              <label className="text-xs font-medium text-foreground-500 mb-1 block">Tipo de Vistoria</label>
              <select
                value={filters.tipo || ""}
                onChange={(e) => onFilterChange("tipo", e.target.value)}
                className={selectClasses}
              >
                <option value="">Todos</option>
                {tiposVistoria.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="text-xs font-medium text-foreground-500 mb-1 block">Status</label>
              <select
                value={filters.status || ""}
                onChange={(e) => onFilterChange("status", e.target.value)}
                className={selectClasses}
              >
                <option value="">Todos</option>
                {statusVistoria.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Estado fitossanitário */}
            <div>
              <label className="text-xs font-medium text-foreground-500 mb-1 block">Estado Fitossanitário</label>
              <select
                value={filters.estado || ""}
                onChange={(e) => onFilterChange("estado", e.target.value)}
                className={selectClasses}
              >
                <option value="">Todos</option>
                {estadosFitossanitarios.map((e) => (
                  <option key={e} value={e}>{e.charAt(0).toUpperCase() + e.slice(1)}</option>
                ))}
              </select>
            </div>

            {/* Classe de risco */}
            <div>
              <label className="text-xs font-medium text-foreground-500 mb-1 block">Classe de Risco</label>
              <select
                value={filters.risco || ""}
                onChange={(e) => onFilterChange("risco", e.target.value)}
                className={selectClasses}
              >
                <option value="">Todas</option>
                {classesRisco.map((r) => (
                  <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                ))}
              </select>
            </div>

            {/* Técnico */}
            <div>
              <label className="text-xs font-medium text-foreground-500 mb-1 block">Técnico</label>
              <select
                value={filters.tecnico || ""}
                onChange={(e) => onFilterChange("tecnico", e.target.value)}
                className={selectClasses}
              >
                <option value="">Todos</option>
                {tecnicos.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 pt-1">
            <button onClick={() => onFilterChange("apply", "true")} className="btn-primary text-xs px-3 py-1.5">
              <i className="ri-search-line"></i> Aplicar Filtros
            </button>
            {hasActiveFilters && (
              <button onClick={onClear} className="btn-outline text-xs px-3 py-1.5">
                <i className="ri-close-line"></i> Limpar
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}